import React, { useState } from 'react';
import { Modal, TextField } from '@mui/material';
import { useMutation } from '@apollo/client';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { UPDATE_USER_PROFILE } from 'api';
import PartialLoader from 'components/layout/PartialLoader';
import ModalContainer from 'components/ModalContainer';
import { Button, openNotification } from 'tt-ui-lib/core';
import styles from './EmailChangeModal.module.scss';

const EMAIL_CHANGE_FORM_ID = 'emailChange';

const validationSchema = Yup.object().shape({
  email: Yup.string().required('Email is required.').email('Email is invalid'),
});

const EmailChangeModal = ({
  isModelOpen,
  handleClose,
  setIsOpen,
  currentEmail,
  onEmailChanged,
}) => {
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);

  const formOptions = {
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: '',
    },
  };
  const formMethods = useForm(formOptions, { mode: 'all' });

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = formMethods;

  const [updateUserProfile] = useMutation(UPDATE_USER_PROFILE);

  const onSubmit = async (data) => {
    try {
      setIsUpdateLoading(true);
      const res = await updateUserProfile({
        variables: {
          input: {
            email: data.email,
          },
        },
      });

      if (res?.data?.updateUserProfile) {
        setIsUpdateLoading(false);
        setIsOpen(false);
        reset();
        openNotification({
          message:
            'Verification email has been sent to your new email address. Please check your inbox.',
          type: 'success',
        });
        if (onEmailChanged) {
          onEmailChanged(res.data.updateUserProfile);
        }
      }
      setIsUpdateLoading(false);
    } catch (e) {
      console.log(e);
      setIsUpdateLoading(false);
      setIsOpen(false);
      reset();
      // Always show success message for security - don't reveal if email exists
      openNotification({
        message:
          'Verification email has been sent to your new email address. Please check your inbox.',
        type: 'success',
      });
    }
  };

  const onClose = () => {
    if (!isUpdateLoading) {
      reset();
      setIsOpen(false);
    }
  };

  return (
    <>
      {isModelOpen && (
        <Modal
          open={isModelOpen}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          classes={{
            root: styles.modalRoot,
          }}
          BackdropProps={{ style: { backgroundColor: 'transparent' } }}
        >
          <ModalContainer
            className={styles.emailModalWrapper}
            handleClose={onClose}
            isPasswordUpdateModal
            isScroll
          >
            <div className={styles.modalTitle}>Change your email</div>
            <div className={styles.modalDescription}>
              Enter your new email address. A verification link will be sent to confirm the change.
            </div>
            <div className={styles.currentEmail}>
              Current email: <strong>{currentEmail}</strong>
            </div>
            <FormProvider {...formMethods}>
              <form id={EMAIL_CHANGE_FORM_ID} className={styles.formWrapper}>
                <div className={styles.inputWrapper}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field: { value, onChange, ...field } }) => (
                      <TextField
                        {...field}
                        className={styles.modal_input}
                        label="New email"
                        type="email"
                        value={value}
                        onChange={onChange}
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        autoFocus
                      />
                    )}
                  />
                </div>
              </form>
            </FormProvider>
            <Button
              type="primary"
              className={styles.changeBtn}
              form={EMAIL_CHANGE_FORM_ID}
              onClick={handleSubmit(onSubmit)}
            >
              Send verification email
            </Button>
            <PartialLoader loading={isUpdateLoading} isLightContainer />
          </ModalContainer>
        </Modal>
      )}
    </>
  );
};

export default EmailChangeModal;
